/**
 * useCaseWorkflow — FE-WIRING-1B
 * ================================
 *
 * Server-state hook for the clinic/coordinator canonical operational
 * workflow view. Reads:
 *
 *   - GET /api/milestone-instances/case/{case_id}     (COM-1D.D)
 *   - GET /api/milestone-tasks/case/{case_id}         (COM-1D.D2)
 *
 * And exposes guarded mutators for the canonical task actions:
 *
 *   - POST   /api/milestone-tasks                           (create)
 *   - POST   /api/milestone-tasks/{id}/transition           (transition)
 *   - POST   /api/milestone-tasks/{id}/assign               (assign)
 *   - POST   /api/milestone-tasks/{id}/transfer             (transfer)
 *   - POST   /api/milestone-tasks/{id}/suppress             (suppress)
 *   - GET    /api/milestone-tasks/{id}/history              (assignment_log)
 *   - POST   /api/milestone-instances/{id}/transition       (milestone status)
 *
 * STRICT BOUNDARIES (FE-CONTRACT-LOCK):
 *   - R-1: no parallel workflow store (only cached server payload).
 *   - R-5: writes flow through canonical HTTP routes only.
 *   - R-6: no fallback / shadow / autoheal chains; errors surfaced.
 *   - R-7/R-13: patient role NEVER calls /api/milestone-tasks/*; the
 *     server returns 403 forbidden_role and this hook surfaces the error.
 *   - R-11: TimelineContext is not imported.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';

export function useCaseWorkflow(caseId) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState([]);

  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    if (!caseId) {
      setLoading(false);
      setMilestones([]);
      setTasks([]);
      return;
    }
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const [miRes, mtRes] = await Promise.all([
        api.get(`/milestone-instances/case/${caseId}`),
        api.get(`/milestone-tasks/case/${caseId}`),
      ]);
      if (seq !== reqSeq.current) return;

      const miPayload = (miRes && miRes.data) || {};
      const mtPayload = (mtRes && mtRes.data) || {};

      if (miPayload.success !== true) {
        throw new Error('canonical_milestone_read_failed');
      }
      if (mtPayload.success !== true) {
        throw new Error('canonical_task_read_failed');
      }
      setMilestones(Array.isArray(miPayload.milestones) ? miPayload.milestones : []);
      setTasks(Array.isArray(mtPayload.tasks) ? mtPayload.tasks : []);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError(e);
      setMilestones([]);
      setTasks([]);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  // ─── Canonical mutators (write-doors via HTTP routes only — R-5) ──────
  const transitionMilestone = useCallback(
    async (milestoneId, targetStatus, extra = {}) => {
      const res = await api.post(`/milestone-instances/${milestoneId}/transition`, {
        target_status: targetStatus,
        ...extra,
      });
      await load();
      return res.data;
    },
    [load]
  );

  const createTask = useCallback(
    async (payload) => {
      const res = await api.post('/milestone-tasks', payload);
      await load();
      return res.data;
    },
    [load]
  );

  const transitionTask = useCallback(
    async (taskId, targetStatus, extra = {}) => {
      const res = await api.post(`/milestone-tasks/${taskId}/transition`, {
        target_status: targetStatus,
        ...extra,
      });
      await load();
      return res.data;
    },
    [load]
  );

  const assignTask = useCallback(
    async (taskId, body) => {
      const res = await api.post(`/milestone-tasks/${taskId}/assign`, body || {});
      await load();
      return res.data;
    },
    [load]
  );

  const transferTask = useCallback(
    async (taskId, body) => {
      const res = await api.post(`/milestone-tasks/${taskId}/transfer`, body || {});
      await load();
      return res.data;
    },
    [load]
  );

  const suppressTask = useCallback(
    async (taskId, body) => {
      const res = await api.post(`/milestone-tasks/${taskId}/suppress`, body || {});
      await load();
      return res.data;
    },
    [load]
  );

  const fetchTaskHistory = useCallback(async (taskId) => {
    const res = await api.get(`/milestone-tasks/${taskId}/history`);
    return res.data;
  }, []);

  // R6-IMPL-003A — precondition write-doors. All gated server-side
  // behind require_role(WRITE_ROLES) + clinic-scope check.
  const addDocumentRequirement = useCallback(
    async (taskId, body) => {
      const res = await api.post(`/milestone-tasks/${taskId}/preconditions/document`, body || {});
      await load();
      return res.data;
    },
    [load]
  );
  const addSignatureRequirement = useCallback(
    async (taskId, body) => {
      const res = await api.post(`/milestone-tasks/${taskId}/preconditions/signature`, body || {});
      await load();
      return res.data;
    },
    [load]
  );
  const satisfyRequirement = useCallback(
    async (taskId, requirementId, body) => {
      const res = await api.post(
        `/milestone-tasks/${taskId}/preconditions/${requirementId}/satisfy`,
        body || {},
      );
      await load();
      return res.data;
    },
    [load]
  );
  const cancelRequirement = useCallback(
    async (taskId, requirementId) => {
      const res = await api.delete(`/milestone-tasks/${taskId}/preconditions/${requirementId}`);
      await load();
      return res.data;
    },
    [load]
  );

  return {
    loading,
    error,
    milestones,
    tasks,
    refetch: load,
    transitionMilestone,
    createTask,
    transitionTask,
    assignTask,
    transferTask,
    suppressTask,
    fetchTaskHistory,
    addDocumentRequirement,
    addSignatureRequirement,
    satisfyRequirement,
    cancelRequirement,
  };
}

/**
 * Canonical enum → user-facing label & badge color.
 * Milestone enum: pending | scheduled | in_progress | achieved | skipped | blocked.
 */
export const MILESTONE_STATUS_DISPLAY = {
  pending: { label: 'Pending', badgeClass: 'bg-slate-100 text-slate-600 border-slate-200' },
  scheduled: { label: 'Scheduled', badgeClass: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  in_progress: { label: 'In Progress', badgeClass: 'bg-blue-100 text-blue-700 border-blue-200' },
  achieved: { label: 'Completed', badgeClass: 'bg-green-100 text-green-700 border-green-200' },
  skipped: { label: 'Skipped', badgeClass: 'bg-slate-100 text-slate-500 border-slate-200' },
  blocked: { label: 'Blocked', badgeClass: 'bg-red-100 text-red-700 border-red-200' },
};

/**
 * Canonical task enum → user-facing label & badge color.
 * Task enum (COM-1D.D2): assigned | in_progress | done | blocked | cancelled.
 */
export const TASK_STATUS_DISPLAY = {
  assigned: { label: 'Assigned', badgeClass: 'bg-slate-100 text-slate-700 border-slate-200' },
  in_progress: { label: 'In Progress', badgeClass: 'bg-blue-100 text-blue-700 border-blue-200' },
  done: { label: 'Done', badgeClass: 'bg-green-100 text-green-700 border-green-200' },
  blocked: { label: 'Blocked', badgeClass: 'bg-red-100 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', badgeClass: 'bg-slate-100 text-slate-500 border-slate-200' },
};

// Canonical state-machine maps — used for FE next-status pickers only.
// FE never invents transitions; it only offers the canonical ones.
export const MILESTONE_NEXT_STATUSES = {
  pending: ['scheduled', 'in_progress', 'skipped', 'blocked'],
  scheduled: ['in_progress', 'skipped', 'blocked'],
  in_progress: ['achieved', 'skipped', 'blocked'],
  blocked: ['scheduled', 'in_progress', 'skipped'],
  achieved: [],
  skipped: [],
};

export const TASK_NEXT_STATUSES = {
  assigned: ['in_progress', 'blocked', 'cancelled'],
  in_progress: ['done', 'blocked', 'cancelled'],
  blocked: ['assigned', 'in_progress', 'cancelled'],
  done: [],
  cancelled: [],
};

// Role buckets mirror server-side COM-1D.D2 sets (FE display gate only —
// server is the sole enforcer via HTTP 403).
export const TASK_WRITE_ROLES = new Set([
  'treatx_super_admin',
  'treatx_admin',
  'provider_admin',
  'provider_coordinator',
  'provider_coordinator',
  'provider_after_sales_rep',
  'provider_sales_rep',
]);

export default useCaseWorkflow;
